import { memo, useCallback } from 'react';

import { useTrackAgentNextActionPillClicked } from '@air/analytics';

import { useWorkspaceAgentGuidedVariantFlowStore } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantProvider';
import {
  getWorkspaceAgentHeroStructuredDisplay,
  getWorkspaceAgentPanelZeroStateSuggestionById,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelZeroStateSuggestions';
import { WorkspaceAgentNextActions } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/next-actions/WorkspaceAgentNextActions';
import { useWorkspaceAgentCreditsDepleted } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentCreditsDepleted';
import { usePrivateWorkspaceAgentPanelComposerPills } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { useWorkspaceAgentComposerSeed } from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentComposerSeedProvider';

export const PrivateWorkspaceAgentVariantNextActionsBody = memo(() => {
  const { hasExplicitSelection } = usePrivateWorkspaceAgentPanelComposerPills();
  const { blockIfCreditsDepleted } = useWorkspaceAgentCreditsDepleted();
  const { seedComposer } = useWorkspaceAgentComposerSeed();
  const { trackAgentNextActionPillClicked } = useTrackAgentNextActionPillClicked();
  const openGuidedVariantFlow = useWorkspaceAgentGuidedVariantFlowStore((state) => state.openGuidedVariantFlow);

  const handleFollowUp = useCallback(
    (suggestionId: string) => {
      if (blockIfCreditsDepleted()) {
        return;
      }

      const suggestion = getWorkspaceAgentPanelZeroStateSuggestionById(suggestionId);

      if (!suggestion) {
        return;
      }

      seedComposer(suggestion.prompt, {
        sendImmediately: hasExplicitSelection,
        source: 'hero_chip',
        structuredDisplay: hasExplicitSelection ? getWorkspaceAgentHeroStructuredDisplay(suggestionId) : undefined,
      });
    },
    [blockIfCreditsDepleted, hasExplicitSelection, seedComposer],
  );

  const handleRename = useCallback(() => {
    if (blockIfCreditsDepleted()) {
      return;
    }

    trackAgentNextActionPillClicked({ pill_id: 'rename' });
    handleFollowUp('hero-bulk-rename');
  }, [blockIfCreditsDepleted, handleFollowUp, trackAgentNextActionPillClicked]);
  const handleTag = useCallback(() => {
    if (blockIfCreditsDepleted()) {
      return;
    }

    trackAgentNextActionPillClicked({ pill_id: 'tag' });
    handleFollowUp('hero-bulk-tag-metadata');
  }, [blockIfCreditsDepleted, handleFollowUp, trackAgentNextActionPillClicked]);
  const handleMakeVariants = useCallback(() => {
    if (blockIfCreditsDepleted()) {
      return;
    }

    trackAgentNextActionPillClicked({ pill_id: 'make_variants' });
    openGuidedVariantFlow();
  }, [blockIfCreditsDepleted, openGuidedVariantFlow, trackAgentNextActionPillClicked]);

  return <WorkspaceAgentNextActions onMakeVariants={handleMakeVariants} onRename={handleRename} onTag={handleTag} />;
});

PrivateWorkspaceAgentVariantNextActionsBody.displayName = 'PrivateWorkspaceAgentVariantNextActionsBody';
